/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2026 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ERROR_CODE,
  handleNetworkFailure,
  notifyRequestSucceeded,
} from "@advapacs/common";
import axios, { Axios, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { LoginAction } from "../pages/login/ducks/slices";
import { SignUpPath } from "../pages/signup";
import { ErrorAction, ApiErrorItem } from "../reducers/slices/errorSlice";
import { CustomAxiosError } from "../types/common";
import GroupPromise from "./GroupPromise";
import { authenticatedUserApi } from "./api";

const TIMEOUT = 1 * 60 * 1000;
const NETWORK_SOURCE = "region";

// Pages where an UNAUTHENTICATED error doesn't log the user out.
const NO_LOGOUT_PATHS: string[] = [
  "/",
  "/SSO/SAML2",
  SignUpPath.INDIVIDUAL,
  SignUpPath.ORGANISATION,
  SignUpPath.SELECT,
  "/ForgotPassword",
  "/RegisterComplete",
];

// Pages where a 401 doesn't trigger a token refresh.
const NO_REFRESH_PATHS: string[] = [
  SignUpPath.INDIVIDUAL,
  SignUpPath.ORGANISATION,
  "/ResetPassword",
  "/ForgotPassword",
  "/RegisterComplete",
  "/Auth/EmailValidation",
  SignUpPath.SELECT,
  "/SSO/SAML2",
  "/Studies/Share",
];

// 401 codes that aren't an expired session, and the one that always is.
const NON_EXPIRY_CODES = ["1003", "1004", "1005", "1013"];
const TOKEN_EXPIRED_CODE = "1007";

interface ApiErrorData {
  code?: string;
  errors?: ApiErrorItem[];
}

const axiosInstanceRegion: Axios = axios.create();
axiosInstanceRegion.defaults.withCredentials = true;

const trimStringParams = (config: InternalAxiosRequestConfig) => {
  if (config.params) {
    Object.keys(config.params).forEach((key) => {
      if (config.params[key] && typeof config.params[key] === "string") {
        config.params[key] = config.params[key].trim();
      }
    });
  }
  return config;
};

const getRefreshTokenUrl = () => {
  const region = localStorage.getItem("region") || "";
  return region
    ? `${authenticatedUserApi.REFRESH_TOKEN}?region=${region}`
    : authenticatedUserApi.REFRESH_TOKEN;
};

const setUpAxiosInstance = (store: any, baseUrl: string) => {
  const loginState = store.getState().loginReducer;
  const { defaults } = axiosInstanceRegion;
  defaults.timeout = TIMEOUT;
  defaults.baseURL = baseUrl;
  defaults.headers.common["X-XSRF-TOKEN"] = loginState?.csrfToken;
  defaults.headers.common["Accept-Language"] =
    loginState?.account?.localisation?.locale || window.navigator.language;
  defaults.headers.common["X-TZ"] =
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const clearAuthentication = () =>
    store.dispatch(LoginAction.clearAuthentication({ isReloadBrowser: false }));

  const onResponseSuccess = (response: AxiosResponse) => {
    notifyRequestSucceeded(NETWORK_SOURCE);
    return response;
  };

  const onResponseError = async (err: CustomAxiosError) => {
    const status = err?.response?.status;
    if (status === undefined) {
      // Shown by NetworkStatusAlert ("Unable to connect to API" / offline).
      return handleNetworkFailure(err, { source: NETWORK_SOURCE });
    }

    const apiError = err?.response?.data as ApiErrorData | undefined;
    const sessionHasBeenFetched =
      store.getState().loginReducer?.sessionHasBeenFetched;
    const { pathname } = window.location;

    store.dispatch(ErrorAction.setError(apiError?.errors));

    if (
      apiError?.code === ERROR_CODE.UNAUTHENTICATED &&
      sessionHasBeenFetched &&
      !NO_LOGOUT_PATHS.includes(pathname)
    ) {
      clearAuthentication();
      localStorage.setItem("path", pathname);
      window.location.reload();
    }

    if (apiError?.code === ERROR_CODE.IP_FORBIDDEN) {
      store.dispatch(LoginAction.restrictIp());
    }

    const isSessionExpired =
      status === 401 &&
      !NO_REFRESH_PATHS.includes(pathname) &&
      ((sessionHasBeenFetched &&
        !NON_EXPIRY_CODES.includes(apiError?.code ?? "")) ||
        apiError?.code === TOKEN_EXPIRED_CODE);

    const originalRequest = err.config;
    if (isSessionExpired && !originalRequest?._retry) {
      originalRequest._retry = true;
      const refreshPromise = GroupPromise.execute(getRefreshTokenUrl());
      if (refreshPromise) {
        try {
          await refreshPromise;
        } catch {
          clearAuthentication();
        }
        return axios.request(originalRequest); // Call by global
      }
    }

    return Promise.reject(err);
  };

  // @ts-ignore
  axiosInstanceRegion.interceptors.request.use(trimStringParams);
  axiosInstanceRegion.interceptors.response.use(
    onResponseSuccess,
    onResponseError
  );
};

export { axiosInstanceRegion, setUpAxiosInstance };
